import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import {
  createCategoryAxis,
  createChartRoot,
  createColumnSeriesCollection,
  createToggleLegend,
  createValueAxis,
  createXYChart,
  SharedColumnSeriesDefinition,
} from '../../Charts/amcharts-xy';
import { ModeAffichageGraphique } from '../../Services/installation';
import { ParcProductionCumuleeService } from '../../Services/parc-production-cumulee.service';
import { EtatService } from '../../Services/etat';
import { ParcProductionCumuleeDonnees, ParcProductionCumuleePoint } from '../../Services/parc-production-cumulee.types';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay';
import { ChartFullscreenModalComponent } from '../chart-fullscreen-modal/chart-fullscreen-modal';
import { ChartPeriodSelectorComponent, PeriodeChangee } from '../chart-period-selector/chart-period-selector';

type ChartPoint = {
  date: string;
  production: number;
  productibleEstime: number;
};

@Component({
  selector: 'app-parc-production-cumulee',
  imports: [CommonModule, SpinnerOverlayComponent, ChartFullscreenModalComponent, ChartPeriodSelectorComponent],
  templateUrl: './parc-production-cumulee.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './parc-production-cumulee.css',
})
export class ParcProductionCumuleeComponent implements OnDestroy {
  @ViewChild('chartHost', { static: true }) private chartHostRef?: ElementRef<HTMLDivElement>;

  private readonly parcProductionCumuleeService = inject(ParcProductionCumuleeService);
  private readonly etat = inject(EtatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);

  private chartRoot?: am5.Root;
  private chartData: ChartPoint[] = [];
  private requeteCourante = 0;

  mode: ModeAffichageGraphique = 'mois';
  dateFin = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  chargement = false;
  messageGraphique = '';
  menuOuvert = false;
  modalVisible = false;
  modalRefreshToken = 0;

  readonly modesDisponibles: ModeAffichageGraphique[] = ['jour', 'mois'];
  readonly cumuleeChartRenderer = (host: HTMLDivElement): am5.Root =>
    this.creerGraphique(host, this.chartData, this.mode);

  constructor() {
    effect(() => {
      const parc = this.etat.parcSelectionne();
      const requeteId = ++this.requeteCourante;
      this.renderiserGraphique([]);
      this.modalVisible = false;
      if (!parc) {
        this.chargement = false;
        this.messageGraphique = '';
        this.cdr.markForCheck();
        return;
      }
      void this.chargerEtRenderiser(parc.id, requeteId);
    });
  }

  @HostListener('document:click')
  fermerMenu() {
    this.menuOuvert = false;
    this.cdr.markForCheck();
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    this.menuOuvert = !this.menuOuvert;
  }

  ouvrirModal() {
    this.menuOuvert = false;
    this.modalVisible = true;
    this.cdr.markForCheck();
  }

  onPeriodeChange({ mode, dateFin }: PeriodeChangee) {
    this.mode = mode;
    this.dateFin = dateFin;
    const parc = this.etat.parcSelectionne();
    if (!parc) return;
    const requeteId = ++this.requeteCourante;
    void this.chargerEtRenderiser(parc.id, requeteId);
  }

  ngOnDestroy() {
    ++this.requeteCourante;
    this.chartRoot?.dispose();
  }

  private async chargerEtRenderiser(parcId: number, requeteId: number) {
    this.chargement = true;
    this.messageGraphique = '';
    this.cdr.markForCheck();

    try {
      const result = await this.avecTimeout(
        this.parcProductionCumuleeService.getParcProductionCumulee(parcId, this.mode, this.dateFin),
        10000
      );
      if (requeteId !== this.requeteCourante || parcId !== this.etat.parcSelectionne()?.id) return;

      const points = this.mapperDonnees(result);
      this.messageGraphique = points.length === 0
        ? 'Les données de production cumulée du parc sont indisponibles pour le moment.'
        : '';
      this.renderiserGraphique(points);
    } catch (error) {
      if (requeteId !== this.requeteCourante) return;
      console.error('Erreur chargement production cumulée parc', error);
      this.messageGraphique = 'Impossible de charger les données de production cumulée du parc.';
      this.renderiserGraphique([]);
    } finally {
      if (requeteId !== this.requeteCourante) return;
      this.chargement = false;
      this.cdr.markForCheck();
    }
  }

  private async avecTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Timeout chargement production cumulée parc')), timeoutMs);
        }),
      ]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  private mapperDonnees(data: ParcProductionCumuleeDonnees): ChartPoint[] {
    return data.data.map((ligne) => ({
      date: this.normaliserDatePourCategorie(ligne),
      production: ligne.production,
      productibleEstime: ligne.productibleEstime,
    })).filter((ligne) => ligne.date.length > 0);
  }

  private normaliserDatePourCategorie(ligne: ParcProductionCumuleePoint): string {
    const brute = ligne.date.trim();
    if (!brute) return '';

    if (this.mode === 'annee' && /^\d{4}$/.test(brute)) return `${brute}-01-01`;
    if (this.mode === 'mois' && /^\d{4}-\d{2}$/.test(brute)) return `${brute}-01`;
    return brute;
  }

  private renderiserGraphique(chartData: ChartPoint[]) {
    this.chartData = chartData;
    const host = this.chartHostRef?.nativeElement;
    if (!host || !isPlatformBrowser(this.platformId)) return;
    this.chartRoot?.dispose();
    if (chartData.length === 0) {
      this.chartRoot = undefined;
      return;
    }
    this.chartRoot = this.creerGraphique(host, chartData, this.mode);
    if (this.modalVisible) {
      this.modalRefreshToken++;
    }
  }

  private creerGraphique(host: HTMLDivElement, chartData: ChartPoint[], mode: ModeAffichageGraphique): am5.Root {
    const root = createChartRoot(host);
    const chart = createXYChart(root, {
      paddingTop: 10,
      paddingBottom: 8,
      paddingLeft: 6,
      paddingRight: 6,
    });
    const xAxis = createCategoryAxis(root, chart, chartData, {
      categoryField: 'date',
      minGridDistance: mode === 'jour' ? 28 : 55,
    });
    xAxis.get('renderer').labels.template.adapters.add('text', (text, target) => {
      const category = target.dataItem?.get('category' as never) as string | undefined;
      if (!category) return text;
      const d = new Date(category);
      if (isNaN(d.getTime())) return text;

      switch (mode) {
        case 'annee':
          return String(d.getUTCFullYear());
        case 'mois':
          return d.toLocaleDateString('fr-FR', { month: 'short' });
        default:
          return String(d.getUTCDate());
      }
    });

    const yAxis = createValueAxis(root, chart, {
      min: 0,
      extraMax: 0.05,
      minGridDistance: 36,
    });

    yAxis.get('renderer').grid.template.setAll({ strokeOpacity: 0.12 });
    yAxis.children.unshift(
      am5.Label.new(root, {
        text: 'Production cumulée (MWh)',
        fontSize: 11,
        fill: am5.color(0x5d645f),
        rotation: -90,
        y: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const seriesDefinitions: SharedColumnSeriesDefinition<ChartPoint>[] = [
      {
        name: 'Production',
        categoryXField: 'date',
        valueYField: 'production',
        color: 0x4b86ff,
        yAxis,
        tooltipText: `{name}\n{categoryX}: {valueY}`,
        widthPercent: 42,
      },
      {
        name: 'Productible contractuel',
        categoryXField: 'date',
        valueYField: 'productibleEstime',
        color: 0x67c8b2,
        yAxis,
        tooltipText: `{name}\n{categoryX}: {valueY}`,
        widthPercent: 42,
      },
    ];

    const series = createColumnSeriesCollection(root, chart, xAxis, chartData, seriesDefinitions);
    createToggleLegend(root, chart, series, { marginTop: 10 });
    chart.appear(600, 100);
    return root;
  }
}
